import {
  useAssignTitleToMember,
  useGetOrganizationTitles,
  useRemoveTitleFromMember
} from 'api/queries/organizations/titles';
import { useTranslation } from 'react-i18next';
import Button, { ButtonType } from 'legacy/common/components/button/Button';
import Checkbox from 'legacy/common/components/form/Checkbox';
import Modal, { ModalType } from 'legacy/common/components/modal/Modal';
import { useCallback, useMemo, useState } from 'react';
import { MdConfirmationNumber } from 'react-icons/md';
import * as toast from 'legacy/common/utils/toast';
import { GroupMemberDto } from 'legacy/groups/types';
import { useQueryClient } from 'react-query';
import { OrganizationTitleDto } from 'api/dtos/organizations/titles';

interface AssignTitleModalProps {
  onClose: () => void;
  selectedMember: GroupMemberDto;
  organizationId: string;
}

const AssignTitleModal = ({
  onClose,
  selectedMember,
  organizationId
}: AssignTitleModalProps) => {
  const { t } = useTranslation(['organizations']);
  const queryClient = useQueryClient();
  const { data: titles } = useGetOrganizationTitles({
    organizationId
  });

  const [selectedTitles, setSelectedTitles] = useState<string[]>(
    selectedMember.titles
  );

  const assignTitleToMemberMutation = useAssignTitleToMember();
  const removeTitleFromMemberMutation = useRemoveTitleFromMember();

  const handleTitleChange = useCallback(
    (title: OrganizationTitleDto) => {
      const isCurrentlyAssigned = selectedTitles.includes(title.name);

      if (isCurrentlyAssigned) {
        removeTitleFromMemberMutation.mutate(
          {
            organizationId,
            userId: selectedMember.user.id,
            titleId: title.id
          },
          {
            onSuccess: () => {
              toast.showToast({
                type: 'success',
                message: t('titleManagement.titleRemoved', {
                  titleName: title.name,
                  firstName: selectedMember.user.firstName,
                  lastName: selectedMember.user.lastName
                })
              });
              queryClient.invalidateQueries(['orgMembers']);
            }
          }
        );
      } else {
        assignTitleToMemberMutation.mutate(
          {
            organizationId,
            memberId: selectedMember.user.id,
            titles: [title.id]
          },
          {
            onSuccess: () => {
              toast.showToast({
                type: 'success',
                message: t('titleManagement.titleAdded', {
                  titleName: title.name,
                  firstName: selectedMember.user.firstName,
                  lastName: selectedMember.user.lastName
                })
              });
              queryClient.invalidateQueries(['orgMembers']);
            }
          }
        );
      }

      setSelectedTitles((prev) =>
        prev.includes(title.name)
          ? prev.filter((name) => name !== title.name)
          : [...prev, title.name]
      );
    },
    [
      organizationId,
      selectedMember,
      queryClient,
      assignTitleToMemberMutation,
      removeTitleFromMemberMutation,
      selectedTitles
    ]
  );

  const modalContent = useMemo(
    () => (
      <div className="p-8">
        <p className="text-base text-gray-500 mb-10">
          {t('titleManagement.assignDescription')}
        </p>

        {titles?.items?.map((title) => (
          <div
            key={title.id}
            className="mb-8 flex items-center justify-between"
          >
            <p className="text-base text-gray-500">{title.name}</p>
            <Checkbox
              isChecked={selectedTitles.includes(title.name)}
              onClick={() => handleTitleChange(title)}
            />
          </div>
        ))}
      </div>
    ),
    [titles, selectedTitles, handleTitleChange]
  );

  return (
    <Modal
      visible
      onClose={onClose}
      type={ModalType.VERY_SMALL}
      title={t('modals.setTitle')}
      icon={<MdConfirmationNumber size={20} />}
      renderFooter={() => (
        <Button type={ButtonType.Primary} onClick={onClose}>
          {t('modals.close')}
        </Button>
      )}
    >
      {modalContent}
    </Modal>
  );
};

export default AssignTitleModal;
